import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  JobPacket,
  JobPacketDetail,
  PaginatedResponse,
  CreateJobPacketPayload,
  JobPacketStatus,
  JobPacketSendResponse,
  JobPacketAcknowledgeResponse,
} from '@/lib/types';

const CREW_API = '/api/v1/crew';

// ─── Filter Types ───────────────────────────────────────────────────────────

export interface JobPacketFilters {
  page?: number;
  page_size?: number;
  q?: string;
  tech?: number;
  assignment?: number;
  status?: JobPacketStatus | '';
  ordering?: string;
}

function cleanFilters<T extends object>(filters: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );
}

// ─── Job Packet Hooks ───────────────────────────────────────────────────────

export function useJobPackets(filters: JobPacketFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.jobPackets.list(clean),
    queryFn: async (): Promise<PaginatedResponse<JobPacket>> => {
      const response = await apiClient.get<PaginatedResponse<JobPacket>>(
        `${CREW_API}/job-packets/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useJobPacket(id: number) {
  return useQuery({
    queryKey: queryKeys.jobPackets.detail(id),
    queryFn: async (): Promise<JobPacketDetail> => {
      const response = await apiClient.get<JobPacketDetail>(
        `${CREW_API}/job-packets/${id}/`
      );
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateJobPacket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateJobPacketPayload): Promise<JobPacket> => {
      const response = await apiClient.post<JobPacket>(
        `${CREW_API}/job-packets/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.jobPackets.all });
    },
  });
}

export function useSendJobPacket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<JobPacketSendResponse> => {
      const response = await apiClient.post<JobPacketSendResponse>(
        `${CREW_API}/job-packets/${id}/send/`
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.jobPackets.all });
    },
  });
}

export function useSendJobPacketEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<JobPacketSendResponse> => {
      const response = await apiClient.post<JobPacketSendResponse>(
        `${CREW_API}/job-packets/${id}/send-email/`
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.jobPackets.all });
    },
  });
}

export function useAcknowledgeJobPacket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<JobPacketAcknowledgeResponse> => {
      const response = await apiClient.post<JobPacketAcknowledgeResponse>(
        `${CREW_API}/job-packets/${id}/acknowledge/`
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.jobPackets.all });
    },
  });
}
